// ── Bot-aware field action config — C.FIELD.ACTIONS.1 ──
// Chaque bot a ses propres actions spécialisées qui exploitent ses skills OpenClaw.
// OpenClaw agent map: CEOB→ceo, CFOB→cfo, CMOB→cmo, CTOB→cto, CSOB→cso,
//   COOB→coo, CHROB→chrob, CROB→crob, CPOB→cpob, CISOB→cisob, CLOB→clob, CINOB→cinob

import type { LucideIcon } from "lucide-react";
import {
  Brain, RotateCcw, BarChart2, TrendingUp, AlertTriangle,
  Users, Swords, Shield, Layers, GitBranch, Sparkles,
  Heart, Search, Zap, Play, Globe, FileText, Target,
  FlaskConical,
} from "lucide-react";

export interface ActionContext {
  fieldLabel: string;
  fieldValue: string;
  sectionId: string;
  botCode: string;
  autonomyLevel: string;
  autonomyTitle: string;
}

export interface SkillAction {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Tailwind border + text + hover classes for the button */
  colorClass: string;
  buildPrompt: (ctx: ActionContext) => string;
  /** If true, button is disabled when fieldValue is empty */
  requiresValue?: boolean;
  /** If true, shows N1/N2/N3 autonomy badge */
  autonomyBadge?: boolean;
}

// ─────────────────────────────────────────────────────
// 1. CHALLENGER PROMPTS — expert-aware, not generic
// ─────────────────────────────────────────────────────

const BOT_CHALLENGER_PROMPT: Record<string, (ctx: ActionContext) => string> = {
  CEOB: (ctx) =>
    `Tu es CarlOS (CEO). Remets en question la valeur "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CEO précises : alignement stratégique, risques de positionnement, cohérence avec la vision globale.`,

  CFOB: (ctx) =>
    `Tu es Frank (CFO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CFO précises : chiffres, hypothèses financières, ROI attendu, risques de trésorerie.`,

  CMOB: (ctx) =>
    `Tu es Mathilde (CMO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CMO précises : angle marketing, différenciation, message vs cible, canal d'acquisition.`,

  CTOB: (ctx) =>
    `Tu es Tim (CTO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CTO précises : faisabilité technique, dette technique, scalabilité, risques de sécurité.`,

  CSOB: (ctx) =>
    `Tu es Simone (CSO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CSO précises : cohérence stratégique, avantage concurrentiel, hypothèses de marché.`,

  CHROB: (ctx) =>
    `Tu es Hélène (CHRO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CHRO précises : impact sur la culture, capacité d'exécution RH, recrutement nécessaire.`,

  CROB: (ctx) =>
    `Tu es Rich (CRO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CRO précises : impact sur les ventes, objections clients prévisibles, pipeline CRM.`,

  COOB: (ctx) =>
    `Tu es Olivier (COO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions COO précises : faisabilité opérationnelle, processus impactés, goulots d'étranglement.`,

  CPOB: (ctx) =>
    `Tu es Paco (CPO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CPO précises : valeur utilisateur, faisabilité produit, priorisation vs roadmap existante.`,

  CISOB: (ctx) =>
    `Tu es Sébastien (CISO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions CISO précises : vecteurs d'attaque, conformité réglementaire (GDPR, ISO 27001), risques résiduels.`,

  CLOB: (ctx) =>
    `Tu es Loulou (CLO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions légales précises : risques contractuels, conformité, clauses manquantes ou dangereuses.`,

  CINOB: (ctx) =>
    `Tu es Inès (CINO). Remets en question "${ctx.fieldValue || "(vide)"}" pour "${ctx.fieldLabel}" (section ${ctx.sectionId}). ` +
    `Pose 2 questions innovation précises : tendances de fond, disruption potentielle, leverage technologique possible.`,
};

// ─────────────────────────────────────────────────────
// 2. BOT SKILL ACTIONS — per-bot specialized buttons
//    Each bot exploits its OpenClaw agent's capabilities
// ─────────────────────────────────────────────────────

export const BOT_SKILL_ACTIONS: Record<string, SkillAction[]> = {

  // ── CEOB — CarlOS (CEO) — vision, stratégie, pivots ──
  CEOB: [
    {
      id: "ceob.strategie",
      label: "Stratégie",
      icon: Brain,
      colorClass: "border-purple-200 text-purple-600 hover:bg-purple-50",
      buildPrompt: (ctx) =>
        `Développe la stratégie pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Perspective CEO : vision long-terme, positionnement, avantage compétitif durable. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "ceob.scenarios",
      label: "Scénarios",
      icon: RotateCcw,
      colorClass: "border-orange-200 text-orange-600 hover:bg-orange-50",
      buildPrompt: (ctx) =>
        `Pour "${ctx.fieldLabel}" = "${ctx.fieldValue}", propose 3 scénarios de pivot CEO. ` +
        `Scénario conservateur, optimiste, disruptif. Conditions déclenchantes pour chaque.`,
      requiresValue: true,
    },
    {
      id: "ceob.benchmark",
      label: "Benchmark",
      icon: Search,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `CarlOS (CEO) — benchmark pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Comment les leaders du secteur abordent-ils cela ? Meilleures pratiques, différences clés.`,
      requiresValue: false,
    },
  ],

  // ── CFOB — Frank (CFO) — Dexter skills: KPIs, projections, risques ──
  CFOB: [
    {
      id: "cfob.analyser",
      label: "Analyser",
      icon: BarChart2,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Frank (CFO, skill Dexter) — analyse financière pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `KPIs clés, unité économique, burn rate impact. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "cfob.projections",
      label: "Projections",
      icon: TrendingUp,
      colorClass: "border-green-200 text-green-600 hover:bg-green-50",
      buildPrompt: (ctx) =>
        `Frank (CFO) — projections financières pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `3 scénarios : conservateur, réaliste, optimiste. Hypothèses explicites, horizon 12M et 3 ans.`,
      requiresValue: true,
    },
    {
      id: "cfob.risques",
      label: "Risques $",
      icon: AlertTriangle,
      colorClass: "border-orange-200 text-orange-600 hover:bg-orange-50",
      buildPrompt: (ctx) =>
        `Frank (CFO) — risques financiers pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Risques de trésorerie, dérapage budgétaire, dépendances financières. Plan de mitigation chiffré.`,
      requiresValue: true,
    },
  ],

  // ── CMOB — Mathilde (CMO) — 23 skills marketing, firecrawl-search ──
  CMOB: [
    {
      id: "cmob.plan",
      label: "Plan mktg",
      icon: TrendingUp,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Mathilde (CMO, 23 skills marketing) — plan marketing pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Canaux prioritaires, message clé, KPIs, budget indicatif. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "cmob.personas",
      label: "Personas",
      icon: Users,
      colorClass: "border-purple-200 text-purple-600 hover:bg-purple-50",
      buildPrompt: (ctx) =>
        `Mathilde (CMO) — crée 2-3 personas pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Pour chaque : profil démographique, pain points, triggers d'achat, message clé, canal préféré.`,
      requiresValue: false,
    },
    {
      id: "cmob.concurrence",
      label: "Concurrence",
      icon: Swords,
      colorClass: "border-orange-200 text-orange-600 hover:bg-orange-50",
      buildPrompt: (ctx) =>
        `Mathilde (CMO, skill firecrawl-search) — analyse concurrentielle pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Top 3 concurrents directs, positionnement différencié, gaps de marché à exploiter.`,
      requiresValue: false,
    },
  ],

  // ── CTOB — Tim (CTO) — n8n-workflows, vps-admin, sécurité ──
  CTOB: [
    {
      id: "ctob.audit",
      label: "Audit tech",
      icon: Shield,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Tim (CTO) — audit technique pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Risques sécurité, dette technique, conformité, scalabilité. Recommandations prioritaires. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "ctob.architecture",
      label: "Architecture",
      icon: Layers,
      colorClass: "border-teal-200 text-teal-600 hover:bg-teal-50",
      buildPrompt: (ctx) =>
        `Tim (CTO) — architecture pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Stack recommandé, patterns de design, trade-offs scalabilité/maintenabilité, diagramme simplifié.`,
      requiresValue: true,
    },
    {
      id: "ctob.automatiser",
      label: "Automatiser",
      icon: GitBranch,
      colorClass: "border-orange-200 text-orange-600 hover:bg-orange-50",
      buildPrompt: (ctx) =>
        `Tim (CTO, skill n8n-workflows) — automatise "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Workflow n8n ou script : étapes, déclencheurs, intégrations. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
  ],

  // ── CSOB — Simone (CSO) — stratégie, firecrawl-search, SWOT ──
  CSOB: [
    {
      id: "csob.swot",
      label: "SWOT",
      icon: Brain,
      colorClass: "border-purple-200 text-purple-600 hover:bg-purple-50",
      buildPrompt: (ctx) =>
        `Simone (CSO) — SWOT pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Forces, Faiblesses, Opportunités, Menaces. Perspective stratégique marché. Recommandation prioritaire.`,
      requiresValue: false,
    },
    {
      id: "csob.pivots",
      label: "Pivots",
      icon: RotateCcw,
      colorClass: "border-orange-200 text-orange-600 hover:bg-orange-50",
      buildPrompt: (ctx) =>
        `Simone (CSO) — pivots stratégiques pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `3 pivots possibles, conditions déclenchantes, risques, upside de chaque. Signal d'alerte précoce.`,
      requiresValue: true,
    },
    {
      id: "csob.scenarios",
      label: "Scénarios",
      icon: Sparkles,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Simone (CSO) — scénarios d'évolution pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Court terme (6M), moyen terme (18M), long terme (3 ans). Hypothèses clés, indicateurs à surveiller.`,
      requiresValue: true,
    },
  ],

  // ── CHROB — Hélène (CHRO) — RH, culture, recrutement ──
  CHROB: [
    {
      id: "chrob.plan-rh",
      label: "Plan RH",
      icon: Users,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Hélène (CHRO) — plan RH pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Profils à recruter, compétences critiques, timeline, budget RH indicatif. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "chrob.culture",
      label: "Culture",
      icon: Heart,
      colorClass: "border-purple-200 text-purple-600 hover:bg-purple-50",
      buildPrompt: (ctx) =>
        `Hélène (CHRO) — impact culturel pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Alignement avec les valeurs, risques de résistance, plan d'adhésion des équipes, rituels proposés.`,
      requiresValue: false,
    },
    {
      id: "chrob.onboarding",
      label: "Onboarding",
      icon: GitBranch,
      colorClass: "border-green-200 text-green-600 hover:bg-green-50",
      buildPrompt: (ctx) =>
        `Hélène (CHRO) — parcours d'onboarding pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Semaine 1/30/90 jours. Milestones, métriques de succès, buddy system.`,
      requiresValue: false,
    },
  ],

  // ── CROB — Rich (CRO) — ventes, pipeline CRM, firecrawl ──
  CROB: [
    {
      id: "crob.strategie",
      label: "Stratégie",
      icon: TrendingUp,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Rich (CRO) — stratégie commerciale pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `ICP, cycle de vente, objectifs pipeline CRM, quotas indicatifs. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "crob.prospecter",
      label: "Prospecter",
      icon: Search,
      colorClass: "border-green-200 text-green-600 hover:bg-green-50",
      buildPrompt: (ctx) =>
        `Rich (CRO, skill firecrawl-search) — identification prospects pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Profil ICP précis, sources de leads, canaux de prospection, message d'approche.`,
      requiresValue: false,
    },
    {
      id: "crob.objections",
      label: "Objections",
      icon: Swords,
      colorClass: "border-orange-200 text-orange-600 hover:bg-orange-50",
      buildPrompt: (ctx) =>
        `Rich (CRO) — objections commerciales pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Top 5 objections prévisibles, réponse tactique à chaque, script de closing.`,
      requiresValue: true,
    },
  ],

  // ── COOB — Olivier (COO) — processus, n8n-workflows ──
  COOB: [
    {
      id: "coob.processus",
      label: "Processus",
      icon: GitBranch,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Olivier (COO) — processus pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Étapes ordonnées, responsables (RACI), KPIs de performance, points de contrôle qualité. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "coob.automatiser",
      label: "Automatiser",
      icon: Zap,
      colorClass: "border-teal-200 text-teal-600 hover:bg-teal-50",
      buildPrompt: (ctx) =>
        `Olivier (COO, skill n8n-workflows) — plan d'automatisation pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Outils recommandés, gain d'efficacité estimé, effort d'implémentation, quick wins prioritaires.`,
      requiresValue: true,
    },
    {
      id: "coob.kpis",
      label: "KPIs ops",
      icon: BarChart2,
      colorClass: "border-purple-200 text-purple-600 hover:bg-purple-50",
      buildPrompt: (ctx) =>
        `Olivier (COO) — définit les KPIs opérationnels pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `5-7 KPIs mesurables, fréquence de mesure, cibles, seuils d'alerte.`,
      requiresValue: false,
    },
  ],

  // ── CPOB — Paco (CPO) — produit, roadmap, user stories ──
  CPOB: [
    {
      id: "cpob.roadmap",
      label: "Roadmap",
      icon: FileText,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Paco (CPO) — roadmap produit pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Now/Next/Later. Dépendances techniques, valeur utilisateur, critères de succès. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "cpob.stories",
      label: "User stories",
      icon: Users,
      colorClass: "border-purple-200 text-purple-600 hover:bg-purple-50",
      buildPrompt: (ctx) =>
        `Paco (CPO) — user stories pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Format "En tant que [persona], je veux... afin de...". Critères d'acceptation GIVEN/WHEN/THEN.`,
      requiresValue: true,
    },
    {
      id: "cpob.prioriser",
      label: "Prioriser",
      icon: Target,
      colorClass: "border-orange-200 text-orange-600 hover:bg-orange-50",
      buildPrompt: (ctx) =>
        `Paco (CPO) — priorisation pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Matrice Impact/Effort. RICE scoring. Recommandation d'ordre et justification stratégique.`,
      requiresValue: true,
    },
  ],

  // ── CISOB — Sébastien (CISO) — sécurité, conformité ──
  CISOB: [
    {
      id: "cisob.audit",
      label: "Audit sécu",
      icon: Shield,
      colorClass: "border-red-200 text-red-600 hover:bg-red-50",
      buildPrompt: (ctx) =>
        `Sébastien (CISO) — audit sécurité pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Vecteurs d'attaque, OWASP Top 10 applicables, scoring CVSS. Remédiation prioritaire. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
    {
      id: "cisob.conformite",
      label: "Conformité",
      icon: FileText,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Sébastien (CISO) — conformité pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Frameworks applicables (GDPR, ISO 27001, SOC2). Gaps identifiés, plan de remédiation.`,
      requiresValue: false,
    },
    {
      id: "cisob.menaces",
      label: "Menaces",
      icon: AlertTriangle,
      colorClass: "border-orange-200 text-orange-600 hover:bg-orange-50",
      buildPrompt: (ctx) =>
        `Sébastien (CISO) — modélisation des menaces pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `STRIDE : Spoofing, Tampering, Repudiation, Info disclosure, DoS, Elevation. Mesures défensives.`,
      requiresValue: false,
    },
  ],

  // ── CLOB — Loulou (CLO) — juridique, contrats, conformité ──
  CLOB: [
    {
      id: "clob.risques",
      label: "Risques légaux",
      icon: AlertTriangle,
      colorClass: "border-red-200 text-red-600 hover:bg-red-50",
      buildPrompt: (ctx) =>
        `Loulou (CLO) — risques légaux pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Exposition contractuelle, responsabilités, clauses dangereuses. Recommandations préventives.`,
      requiresValue: false,
    },
    {
      id: "clob.contrat",
      label: "Clauses",
      icon: FileText,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Loulou (CLO) — clauses contractuelles pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Clauses essentielles à inclure, formulations recommandées, points de négociation. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
  ],

  // ── CINOB — Inès (CINO) — innovation, tendances, disruption ──
  CINOB: [
    {
      id: "cinob.tendances",
      label: "Tendances",
      icon: Globe,
      colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
      buildPrompt: (ctx) =>
        `Inès (CINO) — veille tendances pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Signaux faibles, technologies émergentes applicables, horizon 2-5 ans. Sources crédibles.`,
      requiresValue: false,
    },
    {
      id: "cinob.disruption",
      label: "Disruption",
      icon: Sparkles,
      colorClass: "border-purple-200 text-purple-600 hover:bg-purple-50",
      buildPrompt: (ctx) =>
        `Inès (CINO) — potentiel disruptif pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `Qui pourrait disrupter cela ? Comment être disrupteur plutôt que disrupté ? Leverage IA/tech disponible.`,
      requiresValue: false,
    },
    {
      id: "cinob.prototype",
      label: "Prototype",
      icon: FlaskConical,
      colorClass: "border-green-200 text-green-600 hover:bg-green-50",
      buildPrompt: (ctx) =>
        `Inès (CINO) — plan de prototypage pour "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
        `MVP expérimental en 2 semaines : hypothèse à tester, métrique de validation, ressources minimales. Autonomie : ${ctx.autonomyTitle}.`,
      requiresValue: true,
      autonomyBadge: true,
    },
  ],
};

// ─────────────────────────────────────────────────────
// 3. DEFAULT FALLBACK — bots non configurés
// ─────────────────────────────────────────────────────

const DEFAULT_SKILL_ACTIONS: SkillAction[] = [
  {
    id: "default.executer",
    label: "Exécuter",
    icon: Play,
    colorClass: "border-blue-200 text-blue-600 hover:bg-blue-50",
    buildPrompt: (ctx) =>
      `Exécute la tâche décrite dans "${ctx.fieldLabel}" : "${ctx.fieldValue}". ` +
      `Autonomie : ${ctx.autonomyTitle}.`,
    requiresValue: true,
    autonomyBadge: true,
  },
  {
    id: "default.rechercher",
    label: "Rechercher",
    icon: Search,
    colorClass: "border-purple-200 text-purple-600 hover:bg-purple-50",
    buildPrompt: (ctx) =>
      `Recherche des informations pertinentes pour "${ctx.fieldLabel}" : "${ctx.fieldValue || "(vide)"}". ` +
      `Sources récentes, données factuelles, exemples concrets applicables.`,
    requiresValue: false,
  },
];

const DEFAULT_CHALLENGER = (ctx: ActionContext) =>
  `Remets en question la valeur "${ctx.fieldValue || "(vide)"}" pour le champ "${ctx.fieldLabel}" ` +
  `(section ${ctx.sectionId}). Pose 2 questions précises pour l'améliorer.`;

// ─────────────────────────────────────────────────────
// 4. HELPERS — API publique du module
// ─────────────────────────────────────────────────────

/** Returns the list of skill action buttons for a given bot */
export function getSkillActions(botCode: string): SkillAction[] {
  return BOT_SKILL_ACTIONS[botCode] ?? DEFAULT_SKILL_ACTIONS;
}

/** Returns the bot-specific challenger prompt */
export function buildChallengerPrompt(ctx: ActionContext): string {
  const fn = BOT_CHALLENGER_PROMPT[ctx.botCode] ?? DEFAULT_CHALLENGER;
  return fn(ctx);
}
